import { documentationContents } from '../content/documentation-contents';
import {
  DocumentationContents,
  DocumentationContentsHeading,
  DocumentationContentsPage,
  DocumentationContentsSection,
  DocumentationContentsSeparator,
} from '../types';

export function isDocumentationContentsSection(
  item: any
): item is DocumentationContentsSection {
  return (
    typeof item === 'object' &&
    'title' in item &&
    'contents' in item &&
    item.contents !== undefined
  );
}

export function isDocumentationContentsPage(
  item: any
): item is DocumentationContentsPage {
  return (
    typeof item === 'object' &&
    'url' in item &&
    'title' in item &&
    !('contents' in item)
  );
}

export function isDocumentationContentsHeading(
  item: any
): item is DocumentationContentsHeading {
  return (
    typeof item === 'object' &&
    'title' in item &&
    !('url' in item) &&
    !('contents' in item)
  );
}

export function isDocumentationContentsSeparator(
  item: any
): item is DocumentationContentsSeparator {
  return item === '-';
}

/**
 * Identifies a section in the contents menu by its position, since titles
 * aren't guaranteed to be unique
 */
export function documentationContentsSectionKey(
  parentKey: string,
  index: number
): string {
  return parentKey ? `${parentKey}_${index}` : `${index}`;
}

/**
 * Get the keys of every section containing (or linking to) the page at the
 * given pathname, outermost first
 */
export function findDocumentationContentsSectionKeys(
  pathname: string,
  contents: DocumentationContents = documentationContents,
  parentKey: string = ''
): string[] {
  const find = (
    contents: DocumentationContents,
    parentKey: string
  ): string[] | null => {
    for (let i = 0; i < contents.length; i++) {
      const item = contents[i];

      if (isDocumentationContentsSection(item)) {
        const key = documentationContentsSectionKey(parentKey, i);
        const descendantKeys = find(item.contents, key);
        if (descendantKeys !== null) {
          return [key, ...descendantKeys];
        }
        if (`/docs${item.url}` === pathname) {
          return [key];
        }
      } else if (
        isDocumentationContentsPage(item) &&
        `/docs${item.url}` === pathname
      ) {
        return [];
      }
    }

    return null;
  };

  return find(contents, parentKey) ?? [];
}

/**
 * Get the contents menu title for the page at the given pathname
 */
export function findDocumentationContentsTitle(
  pathname: string,
  contents: DocumentationContents = documentationContents
): string | undefined {
  for (const item of contents) {
    if (
      (isDocumentationContentsSection(item) ||
        isDocumentationContentsPage(item)) &&
      `/docs${item.url}` === pathname
    ) {
      return item.title;
    }

    if (isDocumentationContentsSection(item)) {
      const title = findDocumentationContentsTitle(pathname, item.contents);
      if (title) {
        return title;
      }
    }
  }

  return undefined;
}
